/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * Raccolta FAQ per l'operatore, mostrata in SupportoPage.
 *
 * Volutamente **statica e dentro il bundle**: la pagina Supporto deve
 * rispondere anche in campo senza rete, dove la richiesta di assistenza via
 * email non parte. Niente fetch, niente DB.
 *
 * Ogni risposta descrive il comportamento REALE dell'app (vedi CONTEXT.md):
 * se una regola cambia nel codice, va aggiornata anche qui.
 *
 * Campi: q (domanda), a (risposta, testo semplice su più righe), tag
 * (parole chiave extra per la ricerca, non mostrate), pagine (in quali schermate
 * la voce è pertinente — vedi PAGINE_FAQ in fondo al file).
 *
 * ⚠️ Una voce NUOVA senza `pagine` finisce solo nell'elenco completo in Supporto:
 * non comparirà nel pulsante FAQ della sua schermata. Compilarlo sempre.
 */

export const FAQ_CATEGORIE = [
  {
    id: "accesso",
    titolo: "Accesso e account",
    voci: [
      {
        q: "Ho registrato l'account ma il login dice che non posso entrare",
        a: "La verifica dell'email è obbligatoria. Alla registrazione ricevi un link di conferma, valido 24 ore, e finché non lo apri il login viene rifiutato. Quando provi ad accedere senza aver verificato, l'app ti porta su una schermata dedicata col pulsante \"Reinvia email di verifica\": usalo se il link non è arrivato o è scaduto, e controlla anche la cartella spam.",
        pagine: ["profilo"],
        tag: "403 verifica email attivazione conferma",
      },
      {
        q: "Ho dimenticato la password",
        a: "Dalla pagina di login usa \"Password dimenticata?\": ricevi via email un link valido 1 ora per impostarne una nuova (24 ore se il reset lo avvia un amministratore). Il link arriva solo via email — nessuno, nemmeno un amministratore, può leggerti la password esistente. Se dopo la conferma compare \"Esito non confermato\", la connessione è caduta prima della risposta: la password potrebbe essere già cambiata, quindi prova ad accedere con quella nuova prima di chiedere un altro link.",
        pagine: ["profilo"],
        tag: "reset recupero credenziali dimenticata",
      },
      {
        q: "Quali requisiti deve avere la password",
        a: "Almeno 8 caratteri, con almeno un numero, una lettera maiuscola e un carattere speciale. La stessa regola vale per registrazione, reset e cambio password dal profilo.",
        pagine: ["profilo"],
        tag: "policy complessità requisiti sicura",
      },
      {
        q: "L'account risulta bloccato dopo alcuni tentativi",
        a: "Dopo 5 tentativi di login falliti consecutivi l'account si blocca per 15 minuti, poi si sblocca da solo. È una protezione contro i tentativi di indovinare la password. Se hai fretta, un amministratore può sbloccarlo subito.",
        pagine: ["profilo"],
        tag: "lockout bloccato tentativi falliti brute force",
      },
      {
        q: "Perché ogni tanto l'app mi chiede di rifare il login",
        a: "La sessione dura 8 ore, poi scade e va rifatto l'accesso. Viene chiusa prima anche dopo un cambio password o un \"disconnetti da tutti i dispositivi\": sono azioni che invalidano le sessioni aperte altrove.",
        pagine: ["profilo"],
        tag: "sessione scaduta token logout 8 ore",
      },
      {
        q: "In registrazione compare \"Email non autorizzata\"",
        a: "La registrazione è ristretta a un elenco di indirizzi. Sono ammessi il personale TAS2 e le sedi VVF censite, che devono usare un indirizzo @vigilfuoco.it, e gli enti convenzionati, che invece possono usare qualunque dominio purché il loro indirizzo sia stato inserito in elenco. Controlla di aver scritto bene l'indirizzo: se è corretto, chiedi all'amministratore di abilitarti.",
        pagine: ["profilo"],
        tag: "email non autorizzata whitelist registrazione vigilfuoco enti dominio rifiutata",
      },
      {
        q: "Cos'è la campanella in Dashboard e il pallino giallo",
        a: "È la bacheca degli avvisi: le novità dell'app, i cambi di procedura, le manutenzioni programmate e le comunicazioni dello staff. Il pallino giallo col numero indica quanti non ne hai ancora letti; aprendo il pannello vengono segnati come letti. È il posto dove mettersi in pari se non entri da qualche giorno, e vale su tutti i tuoi dispositivi: quello che hai letto dal tablet non torna «nuovo» sul telefono.",
        pagine: ["dashboard"],
        tag: "campanella avvisi notifiche pallino giallo novità bacheca cosa è cambiato",
      },
      {
        q: "Ci sono avvisi vecchi che non ho mai visto?",
        a: "No, e non è un difetto: in bacheca compaiono solo gli avvisi pubblicati dopo la tua registrazione, e non più vecchi di sei mesi. A chi si iscrive oggi non servono mesi di comunicazioni arretrate presentate come novità. Se ti risulta che manchi un avviso importante, chiedilo allo staff da «Richiedi assistenza».",
        pagine: ["dashboard"],
        tag: "avvisi vecchi storico arretrati sei mesi registrazione bacheca mancanti",
      },
      {
        q: "Al primo accesso mi ritrovo sulla pagina Profilo",
        a: "È voluto: la prima volta l'app ti chiede di completare qualifica, regione, comando, sede e turno, perché quei dati finiscono nei riepiloghi e nei report del turno. Salvati una volta, dagli accessi successivi parti sempre dalla Dashboard.",
        pagine: ["profilo"],
        tag: "primo accesso profilo dati iniziali completare",
      },
      {
        q: "Come cambio i miei dati o la password",
        a: "Dal menu Profilo. Lì aggiorni qualifica, regione, comando, sede, turno e telefono, e cambi la password indicando prima quella attuale. Dopo il cambio password le altre sessioni aperte vengono chiuse.",
        pagine: ["profilo"],
        tag: "profilo anagrafica modifica dati personali",
      },
      {
        q: "Sulla stessa postazione subentra un collega a metà turno",
        a: "Se c'è rete si fa nel modo normale: logout, login del collega e registrazione del turno in Riepilogo Turno. Se invece sei offline e il login non è possibile, in fondo al menu compare il pulsante \"Cambio turno offline\": indicando chi subentra, la sessione di accesso resta la tua ma le azioni successive vengono attribuite nel registro all'operatore indicato, e il passaggio stesso viene registrato. Con \"Riprendi tu\" torni a operare a tuo nome. Serve a evitare che il lavoro di un collega risulti fatto da te.",
        pagine: ["turno", "profilo"],
        tag: "cambio turno offline postazione condivisa subentra consegne audit attribuzione",
      },
    ],
  },
  {
    id: "operazione",
    titolo: "Operazione",
    voci: [
      {
        q: "Come entro in un'operazione già aperta da un collega",
        a: "Servono il codice di accesso a 5 cifre dell'operazione oppure il QR generato dal TAS2. Inserito il codice, l'operazione compare nel tuo elenco e resta accessibile anche nelle sessioni successive.",
        pagine: ["dashboard"],
        tag: "codice accesso 5 cifre qr entrare partecipare",
      },
      {
        q: "L'operazione è chiusa e non riesco più a modificare niente",
        a: "È voluto: un'operazione chiusa diventa di sola lettura. Continui a vederla e a stampare i report, ma ogni modifica viene rifiutata dal server, non solo nascosta nell'interfaccia. Serve un amministratore per riaprirla.",
        pagine: ["dashboard", "soccorritori", "squadre", "missioni", "cronologico", "scheda-disperso"],
        tag: "sola lettura read only riapertura bloccata 403 modificare",
      },
      {
        q: "Cosa succede ai soccorritori quando chiudo l'operazione",
        a: "Chi risulta ancora ATTIVO o RIENTRATO passa automaticamente a PARTITO con data e ora della chiusura, TAS2 del turno compresi, e l'ultimo turno di presenza aperto viene chiuso. Chi era PENDING o già PARTITO resta com'è.",
        pagine: ["dashboard", "soccorritori"],
        tag: "chiusura partito rientrato turni presenze",
      },
      {
        q: "Ho sbagliato a impostare INTERVENTO invece di ESERCITAZIONE",
        a: "Il tipo si sceglie alla creazione e dopo può cambiarlo solo un amministratore. Non è una limitazione arbitraria: riclassificare un'operazione altera retroattivamente il campione statistico usato per validare il modello POA, quindi il cambio viene sempre registrato nei log.",
        pagine: ["dashboard"],
        tag: "tipo esercitazione intervento cambiare classificazione",
      },
      {
        q: "Il QR di registrazione non accetta più nessuno",
        a: "Quasi sempre l'operazione è stata chiusa: a quel punto il QR rifiuta le nuove registrazioni. Attenzione, nemmeno l'inserimento manuale funziona più, perché un'operazione chiusa è di sola lettura per tutti tranne gli amministratori. Se devono ancora entrare persone, l'operazione va riaperta da un amministratore.",
        pagine: ["dashboard", "soccorritori"],
        tag: "qr non funziona registrazione chiusa 410 scaduto rifiuta",
      },
      {
        q: "Perché non vedo un'operazione che so essere aperta",
        a: "Gli operatori vedono solo le operazioni a cui hanno accesso: quelle che hanno aperto o in cui sono entrati col codice. I ruoli di comando vedono in più quelle della propria area. Se ti serve una specifica operazione, chiedi il codice di accesso al TAS2 che la gestisce.",
        pagine: ["dashboard"],
        tag: "non visibile elenco mancante permessi visibilità",
      },
    ],
  },
  {
    id: "soccorritori",
    titolo: "Soccorritori e squadre",
    voci: [
      {
        q: "Come faccio registrare i soccorritori senza inserirli a mano",
        a: "Usa il QR di auto-registrazione: il soccorritore lo inquadra, compila i suoi dati e arriva nell'elenco in stato PENDING. Il TAS2 lo valida e da quel momento è impiegabile. Puoi validarne molti insieme selezionandoli con le caselle e usando la barra delle azioni.",
        pagine: ["soccorritori"],
        tag: "qr autoregistrazione pending validare massa bulk",
      },
      {
        q: "Cosa vede sul telefono chi si registra col QR",
        a: "Dopo l'invio dei dati la pagina dice che la registrazione è arrivata ed è in attesa di convalida. Finché non lo confermi nell'elenco, quella persona non risulta in campo e il suo pulsante SOS resta spento: un SOS da qualcuno che la sala operativa non ha in elenco manderebbe una squadra a cercare una persona di cui non sa nulla. La pagina sul telefono si aggiorna da sola, quindi appena convalidi l'SOS si sblocca senza che debba riscansionare il QR.",
        pagine: ["soccorritori"],
        tag: "convalida pending attesa sos spento telefono registrazione qr",
      },
      {
        q: "Ho eliminato un soccorritore, può registrarsi di nuovo col QR",
        a: "Sì. Il telefono conserva la registrazione precedente, ma alla riapertura del link verifica se risulta ancora in elenco: se l'hai eliminato, la vecchia sessione viene scartata e gli ricompare il modulo. Prima invece si riapriva sulla pagina di condivisione posizione e la persona si credeva iscritta senza esserlo.",
        pagine: ["soccorritori"],
        tag: "eliminato cancellato ri-registrazione qr sessione telefono modulo",
      },
      {
        q: "Come mi accorgo che qualcuno si è appena registrato col QR",
        a: "Sulla voce Soccorritori del menu compare un pallino giallo e ricevi un avviso a schermo. Aprendo la pagina li trovi nella sezione \"In attesa di validazione\". Conviene validarli subito: finché restano in attesa non sono impiegabili in squadra e non contano nei numeri della Dashboard.",
        pagine: ["soccorritori"],
        tag: "pallino giallo notifica avviso attesa validazione nuovo registrato",
      },
      {
        q: "Posso importare i soccorritori da Excel",
        a: "Sì, dalla pagina Soccorritori c'è l'import da file Excel. È la via più rapida quando hai già l'elenco del personale in arrivo.",
        pagine: ["soccorritori"],
        tag: "import excel xlsx elenco personale",
      },
      {
        q: "Non riesco a modificare o eliminare una squadra",
        a: "L'eliminazione è riservata agli amministratori. La modifica si blocca quando la squadra ha COMPLETATO almeno una battuta: da quel momento la sua composizione è legata a una zona registrata come battuta, cioè a un risultato, e non la può cambiare nessuno — neanche un amministratore. Finché la battuta è ancora in corso, o non ne ha mai chiusa una, la squadra resta modificabile. Se serve una formazione diversa, crea una nuova squadra. Il pulsante spento ti mostra il motivo: nel dettaglio è scritto sopra il pulsante, nell'elenco compare toccandolo.",
        pagine: ["squadre"],
        tag: "squadra eliminare modificare bloccata impegnata cestino",
      },
      {
        q: "Perché non posso scrivere la sigla della squadra",
        a: "La sigla è il tipo della squadra più un numero assegnato in automatico. Il numero è progressivo e univoco fra tutte le squadre, non per tipo: creando due SAR, una cinofila, un'altra SAR e una SMZ ottieni SAR1, SAR2, CINO3, SAR4, SMZ5. Riempie anche i buchi lasciati da squadre eliminate, così non nascono due sigle uguali. Resta scrivibile a mano solo se scegli il tipo \"Altro\".",
        pagine: ["squadre"],
        tag: "sigla nome squadra automatica readonly progressiva numerazione globale tipo",
      },
      {
        q: "L'app mi avvisa che un soccorritore è già in un'altra squadra",
        a: "È un controllo di sicurezza: lo stesso soccorritore non può risultare contemporaneamente in due squadre, altrimenti l'accountability durante la battuta salta. Toglilo dalla squadra precedente, poi assegnalo alla nuova.",
        pagine: ["squadre", "soccorritori"],
        tag: "duplicato doppio avviso warning due squadre",
      },
    ],
  },
  {
    id: "missioni",
    titolo: "Missioni e dispositivi",
    voci: [
      {
        q: "Non trovo l'opzione per passare la missione direttamente a BATTUTA",
        a: "È disattivata di proposito. Il percorso obbligato è DA_BATTERE → IN_BATTUTA → BATTUTA: saltando lo stato intermedio non resterebbe traccia di quando la squadra è entrata in zona, e senza quella traccia la battuta non è ricostruibile a posteriori.",
        pagine: ["missioni"],
        tag: "stato salto da battere in battuta passaggio disabilitato",
      },
      {
        q: "Avvio la battuta e mi chiede di assegnare un dispositivo",
        a: "Per avviare serve almeno un GPS assegnato alla squadra. Ogni soccorritore può ricevere al massimo un GPS e una radio. I dispositivi diventano Impegnati e si liberano da soli a fine battuta o se ritiri la squadra riportando la missione a DA_BATTERE.",
        pagine: ["missioni", "dispositivi"],
        tag: "gps radio obbligatorio assegnazione dispositivi impegnato",
      },
      {
        q: "C'è un pallino giallo sulla voce Dispositivi del menu",
        a: "Vuol dire che nell'operazione non è ancora censito nessun GPS, e senza almeno un GPS non si può avviare nessuna battuta. Apri Dispositivi e inserisci gli apparati che hai a disposizione. Conviene farlo a inizio turno, prima che serva far partire la prima squadra, non quando la squadra è già pronta a muoversi.",
        pagine: ["dispositivi"],
        tag: "pallino giallo dispositivi gps mancante avviso menu censire inizio turno",
      },
      {
        q: "Chi ha avuto in consegna quel GPS o quella radio?",
        a: "Lo trovi nel dettaglio del Dispositivo, che tiene lo storico completo delle assegnazioni: a chi è stato consegnato, per quale missione e quando. Lo stesso storico compare nella scheda della Squadra per nominativo e in quella del Soccorritore. È la ragione per cui i campi non si compilano più a mano: l'inventario è la fonte di verità.",
        pagine: ["dispositivi"],
        tag: "storico dispositivo consegna chi aveva gps radio tracciabilità inventario",
      },
      {
        q: "Non riesco a cambiare il tipo di un dispositivo",
        a: "Il tipo, GPS o RADIO, si blocca quando l'apparato è impegnato o ha già uno storico di assegnazioni: cambiarlo riscriverebbe il passato dell'inventario. Se hai sbagliato a censirlo e non è mai stato usato puoi ancora correggerlo, altrimenti crea la voce corretta.",
        pagine: ["dispositivi"],
        tag: "tipo dispositivo non modificabile bloccato gps radio storico impegnato",
      },
      {
        q: "I campi GPS e RADIO della missione non sono più scrivibili",
        a: "Corretto: ora la fonte di verità è l'inventario dispositivi, non un campo di testo. Assegni gli apparati all'avvio della battuta e la missione li mostra in automatico. Lo storico completo delle assegnazioni lo trovi nel dettaglio del Dispositivo, della Squadra e del Soccorritore.",
        pagine: ["missioni", "dispositivi"],
        tag: "campo gps radio non editabile inventario storico",
      },
      {
        q: "Chi ha portato quella zona a BATTUTA, e quando?",
        a: "Due strade. Sulla missione stessa c'è lo storico dei cambi di stato, con orari: è la via più rapida. Per una ricerca più ampia usa la pagina Logs e filtra per Cambio stato, dove vedi anche l'operatore che ha fatto la modifica. Come operatore vedi i log delle operazioni di cui fai parte, non di altre.",
        pagine: ["missioni", "logs"],
        tag: "storico stato chi ha cambiato logs registro quando tracciabilità",
      },
      {
        q: "Non riesco a eliminare una missione",
        a: "Chi non è amministratore può eliminare solo l'ultima missione creata, e solo se non ha ancora avuto una battuta. Serve a correggere un inserimento sbagliato senza aprire buchi nella numerazione o cancellare attività già svolta.",
        pagine: ["missioni"],
        tag: "eliminare cancellare zona missione ultima cestino",
      },
    ],
  },
  {
    id: "mappa",
    titolo: "Mappa",
    voci: [
      {
        q: "Come cerco un punto sulla mappa",
        a: "La barra di ricerca accetta sia un nome di località sia una coppia di coordinate scritta come lat,lon (per esempio 41.1234,14.5678). Cliccando sulla mappa puoi anche piazzare un pin e leggere le coordinate di quel punto.",
        pagine: ["mappa"],
        tag: "ricerca coordinate località nominatim pin cercare",
      },
      {
        q: "Ci sono troppi nomi sulla mappa: come li nascondo",
        a: "Nel pannello Livelli, ogni gruppo (missioni per stato, disegni, tracce, indizi, riferimenti, soccorritori) ha nell'intestazione un pulsante \"Aa\": un clic nasconde le etichette di tutto il gruppo, lasciando visibili le geometrie; un secondo clic le rimette (col pulsante barrato sono nascoste). È una scelta per gruppo, non per singolo livello: comoda per togliere i nomi delle tracce quando affollano lo schermo. Lo stesso pulsante \"Aa\" c'è, accanto a ogni livello, nelle Impostazioni della vista 3D. Viene ricordata sul dispositivo.",
        pagine: ["mappa"],
        tag: "etichette nomi nascondere tracce pulire vista affollata livelli gruppo aa",
      },
      {
        q: "Come vedo il rilievo in 3D e funziona senza rete",
        a: "Premi \"Vista 3D\" in alto nella mappa: si apre il rilievo in prospettiva con zone, tracce, punti chiave, indizi e aree appoggiati sul terreno. È spenta finché non la apri, quindi non consuma dati né batteria prima. Richiede la connessione (rilievo e immagini arrivano in streaming e non si salvano per l'offline): senza rete il pulsante avvisa e non si apre. In \"Impostazioni 3D\" scegli mappa base, esagerazione del rilievo e qualità grafica (Leggera è la predefinita e consuma meno). Dalle stesse impostazioni accendi e spegni i livelli, compresi heatmap POA, area di ricerca POA e analisi celle, che seguono l'andamento del terreno (heatmap e area POA solo se il POA è attivo; tutte compaiono solo se già calcolate nella mappa 2D). Per inclinare e ruotare trascina con Ctrl premuto, o con due dita sul telefono. È solo consultazione: per disegnare o misurare torna alla mappa 2D con \"Chiudi 3D\". Il rilievo è indicativo, non una misura topografica.",
        pagine: ["mappa"],
        tag: "3d rilievo terreno montagna pendenza prospettiva vista tridimensionale offline",
      },
      {
        q: "Ogni volta che torno sulla mappa perdo i livelli che avevo acceso",
        a: "Non dovrebbe succedere: le mappe base accese, le opacità e i livelli dati vengono ricordati sul dispositivo e riapplicati anche quando la mappa viene ricostruita. Se invece si azzerano davvero, segnalalo con \"Richiedi assistenza\" indicando browser e dispositivo.",
        pagine: ["mappa"],
        tag: "livelli preferenze layer persi opacità base",
      },
      {
        q: "Posso caricare una traccia GPX",
        a: "Sì. La lettura del file avviene sul dispositivo, quindi funziona anche senza rete: la traccia parte per il server appena la connessione torna. I percorsi finiscono nel livello Tracce, i waypoint tra i disegni (e un disegno di tipo punto puoi trasformarlo in missione o in indizio). Prima di salvare si apre una finestra per dare un nome: su ogni traccia scegli la battuta e il nome si compone come MISSIONE_SQUADRA_GPS, per esempio Z1_SAR1_BN1. La scelta è per singola traccia perché un GPS che fa più uscite nello stesso turno produce un file con tracce di battute diverse; se invece il file contiene una sola uscita c'è la scorciatoia \"stessa battuta per tutte\". Nel pannello livelli le tracce sono raggruppate per giorno.",
        pagine: ["mappa"],
        tag: "gpx import traccia waypoint garmin offline battuta nome squadra gps",
      },
      {
        q: "I disegni che faccio sulla mappa li vedono anche gli altri",
        a: "Sì. Poligoni, cerchi, linee e punti sono dati dell'operazione come missioni, indizi e tracce: compaiono sulla mappa dei colleghi collegati senza che debbano ricaricare e li ritrovi cambiando postazione. Su un'operazione chiusa gli strumenti di disegno restano attivi per misurare, ma il tracciato non si può più salvare.",
        pagine: ["mappa"],
        tag: "disegni condivisi altri operatori poligono cerchio linea punto postazione",
      },
      {
        q: "Le tracce che importo le vedono anche gli altri operatori",
        a: "Sì. Le tracce sono dati dell'operazione come missioni, indizi e riferimenti: compaiono sulla mappa dei colleghi collegati senza che debbano ricaricare, e le ritrovi cambiando postazione. Lo stesso vale per i disegni fatti a mano. Su un'operazione chiusa il pulsante di import è spento: le tracce sono dati dell'operazione, e un'operazione chiusa è in sola lettura.",
        pagine: ["mappa"],
        tag: "tracce condivise altri operatori postazione disegni operazione chiusa",
      },
      {
        q: "Come porto le zone su Google Earth o sul GPS Garmin",
        a: "Seleziona i livelli con le caselle e usa \"Esporta livelli\": il KML è per Google Earth e Global Mapper, il GPX per i GPS Garmin. Nel GPX le aree vengono esportate come linee traccia col contorno chiuso, perché i GPS non gestiscono poligoni.",
        pagine: ["mappa", "missioni"],
        tag: "export kml gpx google earth garmin global mapper esportare",
      },
      {
        q: "Cosa sono i marker diversi dalle zone",
        a: "Sono i punti chiave dell'operazione con icona dedicata: il PCA (dalle coordinate dell'operazione), i PUA/UPN presi dalla Scheda Disperso e il punto di salvataggio o ritrovamento preso dai dati di chiusura. Nello stesso gruppo Riferimenti compaiono anche le aree di ricerca: dalla Scheda Disperso, su ogni PUA o UPN, un interruttore disegna il cerchio standard da 1 km centrato su quelle coordinate. Compaiono man mano che quei dati vengono compilati.",
        pagine: ["mappa"],
        tag: "pca pua upn ritrovamento salvataggio icone marker punti area di ricerca cerchio",
      },
      {
        q: "Posso misurare una distanza o un'area",
        a: "Sì, la mappa ha lo strumento di misura per distanze e aree. Utile per stimare rapidamente l'estensione di una zona prima di assegnarla.",
        pagine: ["mappa"],
        tag: "misura distanza area metri ettari",
      },
      {
        q: "La mappa resta grigia e non carica i riquadri",
        a: "Le mappe di sfondo arrivano da internet: con poco campo i riquadri non scaricano. Prova a cambiare mappa base dal pannello Livelli, perché non tutte hanno la stessa copertura, oppure attendi. Le zone, i percorsi e i disegni restano comunque visibili: manca solo lo sfondo. Prima di ricaricare la pagina controlla di non avere azioni in attesa di sincronizzazione.",
        pagine: ["mappa"],
        tag: "mappa grigia tile riquadri non carica sfondo lenta vuota",
      },
    ],
  },
  {
    id: "offline",
    titolo: "Rete assente e sincronizzazione",
    voci: [
      {
        q: "Sono senza campo: perdo quello che sto inserendo?",
        a: "No. Se la rete cade a metà di un'operazione di scrittura, l'azione viene messa in coda sul dispositivo e compare il messaggio \"Connessione persa — azione salvata\". NON ripeterla: quando la rete torna viene inviata da sola, e rifarla creerebbe un doppione.",
        pagine: ["dashboard", "soccorritori", "squadre", "missioni", "cronologico", "scheda-disperso"],
        tag: "offline senza rete coda persa ripetere doppione campo",
      },
      {
        q: "Cos'è l'avviso \"operazioni in attesa\" nel menu",
        a: "Sono le azioni fatte offline e non ancora arrivate al server. Toccando \"vedi\" si apre l'elenco di cosa esattamente non è partito: prima di spegnere il telefono o allontanarti dalla copertura, controlla lì. Finché la coda non è vuota tieni l'app aperta e, appena c'è segnale, riportala in primo piano: su iPhone la sincronizzazione riparte quando l'app torna visibile.",
        pagine: ["dashboard"],
        tag: "badge contatore sidebar coda pending attesa sync elenco iphone",
      },
      {
        q: "Un'azione risulta \"non riuscita\": ho perso quello che avevo scritto?",
        a: "No. Quando il server rifiuta definitivamente un'azione, questa non viene cancellata: finisce nella sezione \"Non riuscite\" e resta lì con il suo contenuto. Da lì decidi tu se rimandarla o scartarla. È fatto apposta perché un testo scritto in campo non sparisca senza lasciare traccia.",
        pagine: ["dashboard"],
        tag: "non riuscite rifiutata fallita persa riprovare scartare coda",
      },
      {
        q: "Mi dice che c'è stato un conflitto e vince il dato del server",
        a: "Significa che, mentre eri offline, qualcun altro ha modificato lo stesso dato. Per non sovrascrivere il lavoro altrui viene tenuta la versione del server, ma la tua non va persa: la ritrovi in \"Non riuscite\" e puoi rileggerla e decidere se reinserirla. Capita soprattutto sugli stati delle zone, dove due operatori aggiornano la stessa missione.",
        pagine: ["dashboard"],
        tag: "conflitto server vince sovrascritto versione perso contemporaneo",
      },
      {
        q: "Posso ricaricare la pagina o uscire se ho azioni in coda?",
        a: "Meglio di no, finché la coda non si è svuotata. Se provi a ricaricare o chiudere, il browser ti chiede conferma proprio per questo motivo: rispondi di restare. Evita anche il Logout da offline con lavoro non sincronizzato, perché rischi di non riuscire a rientrare finché non torni in copertura.",
        pagine: ["dashboard"],
        tag: "ricaricare refresh chiudere logout uscire coda avviso freeze bloccata",
      },
      {
        q: "Devo ricaricare per vedere le modifiche fatte dai colleghi?",
        a: "No. Con la rete attiva le pagine si aggiornano da sole quando un altro operatore cambia qualcosa sulla stessa operazione, di solito entro pochi secondi. Per questo conviene aggiornare gli stati delle zone mentre succedono: chi guarda la mappa dal PCA li vede subito. Anche la mappa si aggiorna, ma senza rimettersi a posto da capo: zoom, inquadratura e livelli che hai scelto restano dove li hai lasciati.",
        pagine: ["dashboard", "missioni", "mappa"],
        tag: "aggiornamento automatico realtime colleghi ricaricare refresh live tempo reale",
      },
      {
        q: "Cosa continua a funzionare senza rete",
        a: "La consultazione dei dati già scaricati, la mappa con le mappe base già viste, l'import GPX e queste FAQ. Non funzionano le ricerche nuove sul server, l'aggiornamento meteo e l'invio della richiesta di assistenza, che ha bisogno di rete per partire.",
        pagine: ["dashboard"],
        tag: "offline cosa funziona pwa consultazione limiti",
      },
      {
        q: "Posso installare TAS Command come app sul telefono",
        a: "Sì, è una PWA: dal browser usa \"Aggiungi a schermata Home\" (su iPhone dal menu Condividi). Ottieni l'icona, l'avvio a schermo intero e il funzionamento offline. È il modo consigliato per l'uso in campo.",
        pagine: ["dashboard", "profilo"],
        tag: "installare pwa home screen icona app telefono tablet",
      },
    ],
  },
  {
    id: "tracking",
    titolo: "Tracking e SOS",
    voci: [
      {
        q: "Il soccorritore si è registrato col QR ma non lo vedo sulla mappa",
        a: "La registrazione non attiva il tracking: la condivisione della posizione è un consenso esplicito e separato. Il soccorritore deve accendere l'interruttore \"Condividi posizione\", che parte sempre spento e può essere revocato in qualsiasi momento.",
        pagine: ["mappa", "soccorritori"],
        tag: "tracking consenso non visibile posizione toggle gdpr",
      },
      {
        q: "La posizione si aggiorna solo se il telefono è sbloccato",
        a: "Sì, ed è un limite dei browser, non dell'app: la posizione viene rilevata solo con l'app in primo piano. Per un tracciamento continuo la strada è la radio Hytera col bridge LRRP, che non dipende dal telefono.",
        pagine: ["mappa", "soccorritori"],
        tag: "schermo spento background posizione ferma limite browser hytera",
      },
      {
        q: "Il soccorritore ha attraversato una zona senza campo",
        a: "Le posizioni raccolte durante il buco di segnale restano sul telefono e vengono inviate alla riconnessione, quindi la traccia del periodo scoperto si ricostruisce. Il marker live però resta fermo all'ultimo punto ricevuto finché la rete non torna.",
        pagine: ["mappa"],
        tag: "buco segnale traccia recupero buffer riconnessione",
      },
      {
        q: "L'SOS funziona anche col tracking spento?",
        a: "Sì. Premere SOS è di per sé un atto esplicito di emergenza, quindi invia la posizione anche a condivisione disattivata. In sala arriva un avviso, un banner rosso sulla mappa col marker sempre visibile e un pallino rosso sulla voce Mappa del menu: l'avviso a schermo passa, il pallino resta finché l'SOS non è preso in carico. Il soccorritore può annullare un SOS partito per errore, l'operatore può prenderlo in carico. L'SOS non è disponibile a chi è ancora in attesa di convalida.",
        pagine: ["mappa", "soccorritori"],
        tag: "sos emergenza tracking spento allarme banner rosso annullare",
      },
      {
        q: "Con il tracking attivo la batteria si scarica in fretta",
        a: "È atteso: GPS, schermo e mappa insieme consumano molto, e la condivisione posizione tiene volutamente lo schermo acceso perché i browser fermano la rilevazione quando l'app va in secondo piano. Per una giornata di ricerca serve un powerbank. Se un soccorritore non deve essere seguito in tempo reale, meglio lasciare la condivisione spenta.",
        pagine: ["mappa", "soccorritori"],
        tag: "batteria consumo powerbank schermo acceso gps carica durata",
      },
    ],
  },
  {
    id: "dati",
    titolo: "Scheda disperso, POA e report",
    voci: [
      {
        q: "Come devo scrivere le coordinate nella Scheda Disperso",
        a: "Col punto decimale e la virgola a separare i due numeri: 41.5123, 14.7890. Vanno bene anche lo spazio o il punto e virgola come separatore, e i prefissi N/E. Non usare la virgola decimale (41,5123, 14,7890): il programma non riesce a distinguerla dal separatore e scarta il punto invece di indovinarlo. Vale per le coordinate del ritrovamento e per quelle di PUA e UPN.",
        pagine: ["scheda-disperso", "missioni"],
        tag: "coordinate formato virgola punto decimale pua upn ritrovamento gps",
      },
      {
        q: "La sala operativa ha già raccolto i dati al telefono: come li richiamo?",
        a: "Nel form Nuova Operazione, appena scegli il Comando VVF compare il riquadro \"Scheda dalla sala operativa\" con le schede che il Comando ha raccolto e che nessuno ha ancora richiamato. Toccane una: alla creazione dell'operazione i suoi dati finiscono nella Scheda Disperso, che parte già compilata, e la Località viene proposta se non l'hai ancora scritta. Se il riquadro non compare, per quel Comando non ci sono schede in attesa. Tocca di nuovo la scheda scelta per non richiamarla più.",
        pagine: ["dashboard", "scheda-disperso"],
        tag: "sala operativa scheda preliminare richiamo precompilata comando provinciale nuova operazione telefonata",
      },
      {
        q: "Ho richiamato una scheda della sala operativa ma l'app dice che non è più disponibile",
        a: "Vuol dire che nel frattempo è sparita: o l'ha richiamata un altro operatore un istante prima aprendo la sua operazione, o erano passati i cinque giorni dopo i quali le schede mai richiamate vengono cancellate da sole. L'operazione non viene creata a metà: l'elenco si aggiorna, la scelta si azzera e puoi creare l'operazione partendo da una scheda bianca.",
        pagine: ["dashboard"],
        tag: "scheda sala operativa non disponibile scaduta gia richiamata errore creazione",
      },
      {
        q: "Quanto restano le schede inserite dalla sala operativa?",
        a: "Cinque giorni dall'ultima modifica. Una scheda che nessuno richiama viene cancellata automaticamente, così l'elenco mostrato a chi apre un'operazione resta fatto di casi ancora utili. Se la sala operativa la aggiorna, il conto riparte. Prima della scadenza l'elenco della Sala Operativa lo segnala in giallo.",
        pagine: ["schede-preliminari", "dashboard"],
        tag: "cinque giorni scadenza cancellazione automatica retention scheda preliminare",
      },
      {
        q: "Chi può inserire le schede della sala operativa e per quale provincia?",
        a: "L'utenza del Comando Provinciale (ruolo COM PROV), e solo per la propria provincia: il Comando della scheda lo assegna il server leggendo il profilo, non è un campo da scegliere. È l'unica schermata in cui quel ruolo scrive — su tutto il resto resta di sola consultazione. Le schede di un altro Comando non si vedono e non si modificano.",
        pagine: ["schede-preliminari"],
        tag: "com prov comando provinciale chi puo inserire provincia permessi sola lettura",
      },
      {
        q: "In Sala Operativa dice che il mio profilo non ha un Comando VVF",
        a: "Per i ruoli di sede il Comando non è un campo che compili tu: lo assegna l'amministratore associando la tua email a un Comando in Admin → Sedi VVF, oppure in Admin → Personale TAS2 se sei personale TAS che presta servizio in sala. Finché quel collegamento non c'è, la scheda non saprebbe in quale provincia nascere e l'inserimento resta bloccato. Appena l'amministratore l'ha fatto, riapri la schermata Sala Operativa: il collegamento avviene da solo, non devi toccare il profilo né rifare il login. Il Comando riconosciuto è scritto sotto il titolo della schermata.",
        pagine: ["schede-preliminari", "profilo"],
        tag: "comando vvf mancante richiesto sedi vvf profilo com prov errore inserimento bloccato",
      },
      {
        q: "Cos'è il pallino giallo accanto a Scheda Disperso nel menu?",
        a: "Dice che mancano ancora i campi obbligatori della Scheda 1, cioè quelli senza cui la scheda non si salva: cognome e nome del disperso, profilo ISRID, data e coordinate del PUA, tempo meteo all'evento. Passando il puntatore sul pallino compare l'elenco di quelli che mancano davvero. Si spegne da solo quando sono tutti compilati — e \"N/D\" conta come risposta, quindi lo spegne. Su una scheda mai aperta il pallino c'è fin da subito: è il promemoria a compilarla.",
        pagine: ["scheda-disperso", "dashboard"],
        tag: "pallino giallo avviso campi obbligatori scheda disperso menu promemoria",
      },
      {
        q: "Nella Scheda Disperso: lascio vuoto o metto N/D?",
        a: "Sono due cose diverse. N/D significa \"ho chiesto e il dato non è disponibile\" e conta come campo compilato; vuoto significa \"domanda ancora senza risposta\". Usare N/D quando serve rende onesta la barra di avanzamento e fa vedere al colpo d'occhio cosa manca davvero.",
        pagine: ["scheda-disperso"],
        tag: "nd non disponibile vuoto compilazione avanzamento scheda",
      },
      {
        q: "Come registro un indizio trovato dalla squadra",
        a: "Dal Cronologico, aggiungendo una comunicazione di tipo INDIZIO: descrivi cosa è stato trovato e inserisci le coordinate. Un indizio geolocalizzato compare sulla mappa per tutti e, con il modello POA attivo, sposta le probabilità sulle zone vicine. Senza coordinate resta solo una nota nel diario: se puoi, mettile sempre.",
        pagine: ["cronologico", "mappa"],
        tag: "indizio clue cronologico comunicazione coordinate mappa poa zaino",
      },
      {
        q: "Che differenza c'è fra Indizio, Punto e Informazione",
        a: "Sono i tre tipi di comunicazione, e dicono che cosa stai registrando. Indizio: chiede le coordinate, compare sulla mappa come punto viola per tutti e con il POA attivo sposta le probabilità sulle zone vicine. Punto: chiede le coordinate e aggiunge un punto ai disegni della mappa, da cui puoi trasformarlo in missione o in indizio; come tutti i disegni lo vedono tutti gli operatori dell'operazione. Informazione: non crea nulla sulla mappa, resta una riga del diario. Accanto al tipo c'è la Modalità, che dice invece come è arrivata la comunicazione: radio, telefono, frontale o altro.",
        pagine: ["cronologico"],
        tag: "tipo modalita indizio punto informazione radio telefono frontale differenza",
      },
      {
        q: "Le comunicazioni vecchie che tipo hanno adesso",
        a: "Sono state convertite senza perdere niente: quella che era RADIO ora è Informazione con modalità Radio, e così per telefono, frontale e altro. Gli indizi restano indizi, con la modalità non indicata: quel dato all'epoca non veniva chiesto e non è stato inventato d'ufficio.",
        pagine: ["cronologico"],
        tag: "comunicazioni vecchie conversione tipo modalita storico radio",
      },
      {
        q: "Cos'è la finestra di sopravvivenza in dashboard",
        a: "È il tempo trascorso dal PUA confrontato con le statistiche ISRID per quel profilo di disperso, corrette per il meteo. È una stima statistica di popolazione per aiutare a dare priorità, non una prognosi sulla persona che state cercando.",
        pagine: ["dashboard", "scheda-disperso"],
        tag: "sopravvivenza ore isrid stima prognosi dashboard",
      },
      {
        q: "Il nome del turno viene proposto da solo: da dove esce?",
        a: "Dall'orario, ma non da orari fissi: l'app usa alba e tramonto reali della località dell'operazione, così un turno notturno d'inverno e uno d'estate non vengono chiamati allo stesso modo. Se il dato non è disponibile ripiega su un calcolo astronomico. Resta comunque una proposta, puoi cambiarla.",
        pagine: ["turno"],
        tag: "nome turno automatico alba tramonto proposto diurno notturno",
      },
      {
        q: "Cambiano solo i TAS2 e la fascia oraria è la stessa: nuovo turno o modifica?",
        a: "Nuovo turno, sempre. Ogni avvicendamento si registra aprendone uno nuovo, anche quando cambiano solo le persone: è così che resta agli atti la successione dei cambi, cioè chi ha condotto la ricerca, da quando e fino a quando. Modificando il turno in corso il passaggio precedente viene sovrascritto e la catena delle consegne si perde. Modifica Turno serve a correggere un dato sbagliato nel turno in corso — un nome scritto male, un'ora errata — non a registrare un cambio.",
        pagine: ["turno"],
        tag: "nuovo turno modifica avvicendamento cambio consegne successione tas2 personale",
      },
      {
        q: "Cos'è il pallino giallo accanto a Riepilogo Turno nel menu?",
        a: "Dice che il turno attivo non copre più la fascia oraria in cui siete: è passato il tramonto e il turno è ancora diurno, oppure è passata l'alba ed è ancora notturno. Entrando in Riepilogo Turno trovi in cima un riquadro giallo che spiega il caso — quale turno è attivo, a che ora è l'alba o il tramonto sul PCA — e il pulsante che apre direttamente il turno nuovo, col nome già proposto giusto. Un turno notturno appena aperto non dà nessun avviso fino all'alba: è quello corretto per tutta la notte. Finché non apri il turno nuovo il pallino resta acceso e non si spegne da solo — sta dicendo che il turno a verbale non è quello che state facendo. Se l'app non conosce né alba e tramonto reali né le medie mensili, il pallino non compare: preferisce tacere che sbagliare.",
        pagine: ["turno", "dashboard"],
        tag: "pallino giallo turno tramonto alba notturno diurno conversione avviso menu",
      },
      {
        q: "Perché la squadra diventa gialla in elenco",
        a: "È il timer di accountability: la squadra è in battuta da più tempo della soglia di rientro impostata sull'operazione, 90 minuti se non è stata cambiata. Non è un errore, è un promemoria per richiedere un contatto radio.",
        pagine: ["squadre", "missioni"],
        tag: "giallo timer rientro accountability soglia 90 minuti squadra",
      },
      {
        q: "Il meteo mostrato è quello di adesso o quello dell'evento?",
        a: "Sul PCA vedi le condizioni attuali, aggiornate dal server. Il meteo al momento della scomparsa è invece un campo che compili tu nella Scheda Disperso, e i due valori pesano in modo diverso: le distanze statistiche usano quello dell'evento, la stima di sopravvivenza il peggiore dei due.",
        pagine: ["dashboard", "scheda-disperso"],
        tag: "meteo attuale evento pca scheda peso differenza",
      },
      {
        q: "Come esporto i dati dell'operazione",
        a: "Dalle pagine di elenco trovi l'export in PDF per la stampa e in Excel per la rielaborazione; dalla mappa l'export KML e GPX dei livelli. I PDF delle esercitazioni riportano sempre la dicitura ESERCITAZIONE, per non confonderli con un intervento reale.",
        pagine: ["dashboard", "soccorritori", "missioni"],
        tag: "export pdf excel stampa report kml esercitazione",
      },
    ],
  },
];

/** Voci in lista piatta, con la categoria di appartenenza già dentro. */
export const FAQ_FLAT = FAQ_CATEGORIE.flatMap((cat) =>
  cat.voci.map((v, i) => ({ ...v, catId: cat.id, catTitolo: cat.titolo, id: `${cat.id}-${i}` }))
);

export const FAQ_COUNT = FAQ_FLAT.length;

// ── FAQ per pagina ────────────────────────────────────────────────────────────
// Le categorie qui sopra sono TEMATICHE, le pagine dell'app no: "dati" tiene
// insieme voci di Scheda Disperso, Cronologico, Dashboard, Turno e Squadre.
// Per questo l'appartenenza alla pagina sta sulla singola voce (campo `pagine`)
// e non sulla categoria: filtrare per categoria darebbe a ogni pagina domande
// che non la riguardano, che è esattamente il rumore che il pulsante deve evitare.

/** Chiavi = `currentPage` in App.js. Il valore è il nome mostrato all'operatore. */
export const PAGINE_FAQ = {
  dashboard:         "Dashboard",
  soccorritori:      "Soccorritori",
  squadre:           "Squadre",
  missioni:          "Zone / Linee / Punti",
  dispositivi:       "Dispositivi",
  mappa:             "Mappa",
  cronologico:       "Cronologico",
  "scheda-disperso": "Scheda Disperso",
  "schede-preliminari": "Sala Operativa",
  turno:             "Riepilogo Turno",
  logs:              "Logs",
  profilo:           "Profilo",
};

/** Voci pertinenti a una pagina, nell'ordine in cui compaiono in elenco. */
export const faqPerPagina = (pagina) =>
  pagina ? FAQ_FLAT.filter((v) => (v.pagine || []).includes(pagina)) : [];

/** Quante ne ha una pagina: serve a non mostrare il pulsante dove non c'è nulla. */
export const contaFaqPagina = (pagina) => faqPerPagina(pagina).length;
